/**
 * cheats.js -- Game Genie / Pro Action Replay cheat codes.
 *
 * Persistence lives in storage.js (the `cheats` IndexedDB store, one row per
 * code, keyed by gameId) and cloud sync in sync.js; this module is just code
 * validation and pushing the currently-enabled set down into the core.
 *
 * The core's cheat engine (see core/frontend/snes9x_web.c's "--- cheats ---"
 * section) has no per-cheat toggle/remove by index -- every core.setCheat()
 * call just appends a new cheat internally. So applyCheats() always starts
 * from core.resetCheats() and replays every ENABLED cheat from scratch, in
 * order. Cheap (a handful of cheats at most per game) and avoids needing to
 * track any mapping between a storage.js id and a core-side index at all.
 */

/**
 * Game Genie: XXXX-XXXX (hex digits either side of the dash). Pro Action
 * Replay: 8 hex digits, no dash. Matches exactly what
 * core/vendor/snes9x-libretro/cheats2.cpp's S9xGameGenieToRaw() /
 * S9xProActionReplayToRaw() accept -- see those for the exact format rules
 * this mirrors, including why a plain "is it hex" check is sufficient for
 * Game Genie too (its letter-substitution alphabet is a full permutation of
 * hex digits, not a distinct character set).
 */
const GAME_GENIE_RE = /^[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}$/;
const PRO_ACTION_REPLAY_RE = /^[0-9A-Fa-f]{8}$/;

export function normalizeCheatCode(raw) {
  return raw.trim().toUpperCase();
}

export function isValidCheatCode(raw) {
  const code = normalizeCheatCode(raw);
  return GAME_GENIE_RE.test(code) || PRO_ACTION_REPLAY_RE.test(code);
}

/**
 * Push every ENABLED cheat in `cheats` down into the core, replacing
 * whatever the core had applied before. Safe to call with an empty ROM
 * loaded or no cheats at all -- core.resetCheats() alone just clears.
 */
export function applyCheats(core, cheats) {
  core.resetCheats();
  let index = 0;
  for (const cheat of cheats) {
    if (!cheat.enabled) continue;
    core.setCheat(index, true, cheat.code);
    index++;
  }
}
